import { type ReactNode } from 'react'

import { NoticeKey } from '../../../models/Notice'
import { MCP_WHAT_YOU_CAN_DO_ARTICLE_URL } from '../../OAuth2App/constants'

export enum McpPromoPage {
  UPCOMING = 'upcoming',
  PAST_MEETINGS = 'pastMeetings',
  CONTACTS = 'contacts',
  INSIGHTS = 'insights',
  INTEGRATION_SETTINGS = 'integrationSettings',
}

export type McpPromoVariant = 'connect' | 'interest' | 'admin'

export type McpPromoCta = { label: string; kind: 'external'; href: string } | { label: string; kind: 'route'; to: string }

export interface McpPromoBlockConfig {
  noticeKey: NoticeKey
  title: string
  subtitle: ReactNode
  cta: McpPromoCta
}

const SEE_HOW_IT_WORKS_CTA: McpPromoCta = { label: 'See how it works', kind: 'external', href: MCP_WHAT_YOU_CAN_DO_ARTICLE_URL }
const CONNECT_ZOCKS_MCP_CTA: McpPromoCta = { label: 'Connect Zocks MCP', kind: 'route', to: '/account/integration-settings/oauth2apps#mcp' }

export const MCP_PROMO_BLOCKS: Record<McpPromoPage, Partial<Record<McpPromoVariant, McpPromoBlockConfig>>> = {
  [McpPromoPage.UPCOMING]: {
    connect: {
      noticeKey: NoticeKey.UPCOMING_MCP_CONNECT_PROMO_BANNER,
      title: "How well do you know the person you're seeing next?",
      subtitle: "Analyze what drives them, what's changed, what you've discussed on any topic and what you've never covered.",
      cta: CONNECT_ZOCKS_MCP_CTA,
    },
    interest: {
      noticeKey: NoticeKey.UPCOMING_MCP_INTEREST_PROMO_BANNER,
      title: "Know the single best move for the client you're seeing next",
      subtitle: "Analyze what they've said about retirement, family or their business, how their view on risk has moved and what to do next.",
      cta: SEE_HOW_IT_WORKS_CTA,
    },
  },
  [McpPromoPage.PAST_MEETINGS]: {
    connect: {
      noticeKey: NoticeKey.PAST_MEETINGS_MCP_CONNECT_PROMO_BANNER,
      title: "Know every client like they're your only one",
      subtitle: "Analyze their motivations, the reasons behind their decisions, what's changed and any gaps.",
      cta: CONNECT_ZOCKS_MCP_CTA,
    },
    interest: {
      noticeKey: NoticeKey.PAST_MEETINGS_MCP_INTEREST_PROMO_BANNER,
      title: "Turn everything they've told you into something you can hand them",
      subtitle: 'Create a proposal or an annual review letter with a presentation. Built from their own words, in your format.',
      cta: SEE_HOW_IT_WORKS_CTA,
    },
  },
  [McpPromoPage.CONTACTS]: {
    connect: {
      noticeKey: NoticeKey.CONTACTS_MCP_CONNECT_PROMO_BANNER,
      title: 'Know why a topic is suddenly coming up with your clients',
      subtitle: "The questions rising across your recent meetings, what's behind them and what to tell clients about it.",
      cta: CONNECT_ZOCKS_MCP_CTA,
    },
    interest: {
      noticeKey: NoticeKey.CONTACTS_MCP_INTEREST_PROMO_BANNER,
      title: "Check whether your clients' worries match what's actually happening",
      subtitle: 'The concerns rising in your recent meetings, compared against current market coverage, with what to tell them either way.',
      cta: SEE_HOW_IT_WORKS_CTA,
    },
  },
  [McpPromoPage.INSIGHTS]: {
    connect: {
      noticeKey: NoticeKey.INSIGHTS_MCP_CONNECT_PROMO_BANNER,
      title: 'Find what clients ask about that your team never brings up',
      subtitle: 'The topics rising in client questions, matched against what your team actually brings up, and the gaps between the two.',
      cta: CONNECT_ZOCKS_MCP_CTA,
    },
    interest: {
      noticeKey: NoticeKey.INSIGHTS_MCP_INTEREST_PROMO_BANNER,
      title: "Turn this month's insights into something actionable",
      subtitle: 'Ask your AI tool for a client newsletter, talking points or a briefing for the team, built from what clients are actually asking.',
      cta: SEE_HOW_IT_WORKS_CTA,
    },
  },
  [McpPromoPage.INTEGRATION_SETTINGS]: {
    admin: {
      noticeKey: NoticeKey.INTEGRATION_SETTINGS_MCP_ADMIN_PROMO_BANNER,
      title: "Let your team analyze any client's full history in Claude, ChatGPT, Copilot and more",
      subtitle: 'Enable it here once. Each person connects their own account and works from the meetings they can already see in Zocks.',
      cta: { ...CONNECT_ZOCKS_MCP_CTA, label: 'Enable Zocks MCP' },
    },
  },
}

export const MCP_PROMO_SOURCE: Record<Exclude<McpPromoPage, McpPromoPage.INTEGRATION_SETTINGS>, 'upcoming' | 'past_meeting' | 'contact' | 'insights'> = {
  [McpPromoPage.UPCOMING]: 'upcoming',
  [McpPromoPage.PAST_MEETINGS]: 'past_meeting',
  [McpPromoPage.CONTACTS]: 'contact',
  [McpPromoPage.INSIGHTS]: 'insights',
}
